import { css } from '@emotion/react'
import { Close, Visibility, Warning, Sort } from '@mui/icons-material'
import { Badge, CircularProgress, Fab, Tooltip } from '@mui/material'
import { matchPath, useLocation } from 'react-router-dom'
import { colors } from '@global/constants'
import { ROUTES } from 'routes'
import { DefineAndGovernActions } from './schema-components/define-and-govern-actions'
import { t } from '@lingui/core/macro'

const header = css`
  display: flex;
  padding: 10px 30px;
  align-items: center;
  border-bottom: 1px solid #dce3e4;
`

const actionButtons = css`
  display: flex;
  gap: 15px;
`

const fabButtons = css`
  display: flex;
  gap: 5px;

  .MuiBadge-badge {
    z-index: 1050;
  }
`

interface Notifications {
  errors: []
  warnings: []
  currentIssue: string
  reorderMode: boolean
}

interface HeaderProps {
  hasErrors: boolean
  notifications: Notifications
  setNotifications: (notifications: Notifications) => void
  setShowPreviewModal: (show: boolean) => void
  displayNotifications: (e: any) => void
  activeStepIndex: number
  configNotifications: Pick<Notifications, 'errors' | 'warnings'>
  readyToValidate: boolean
}

export const Header = ({
  hasErrors,
  notifications,
  setNotifications,
  setShowPreviewModal,
  displayNotifications,
  activeStepIndex,
  configNotifications,
  readyToValidate,
}: HeaderProps) => {
  const location = useLocation()
  const isDefineAndGovernStep = matchPath(location.pathname, ROUTES.ADMIN_TEMPLATE_EDIT_GOVERN)

  return (
    <div css={header} className="justify-end">
      <div id="template-fabs" css={actionButtons} className="justify-between">
        {isDefineAndGovernStep && <DefineAndGovernActions />}
        <div css={fabButtons}>
          <Tooltip
            title={
              readyToValidate
                ? t`Messages regarding this template configuration`
                : t`Validating template`
            }
          >
            <Badge
              color="error"
              badgeContent={notifications.errors.length + configNotifications.errors.length}
              overlap="circular"
              data-testid="validation-badge"
              slotProps={{
                badge: {
                  'data-testid': 'badge-counter',
                },
              }}
            >
              <Fab
                aria-label="add"
                size="medium"
                disabled={Boolean(
                  !notifications.errors.length &&
                    !notifications.warnings.length &&
                    !configNotifications.errors.length,
                )}
                sx={{
                  boxShadow: 'none',
                  color:
                    notifications.errors.length || notifications.warnings.length ? 'black' : '',
                }}
                onClick={readyToValidate ? displayNotifications : undefined}
              >
                {readyToValidate ? (
                  <Warning />
                ) : (
                  <CircularProgress style={{ width: 15, height: 15 }} />
                )}
              </Fab>
            </Badge>
          </Tooltip>
          <Tooltip
            title={
              hasErrors
                ? t`Please resolve all errors to preview this template.`
                : t`Preview how this template will look in the submission grid`
            }
          >
            <span>
              <Fab
                aria-label="add"
                size="medium"
                sx={{
                  color: colors.white,
                  boxShadow: 'none',
                  backgroundColor: hasErrors ? colors.gray.light : colors.purple.main,
                  '&:hover': {
                    backgroundColor: hasErrors ? colors.gray.light : colors.purple.main,
                  },
                }}
                disabled={Boolean(hasErrors)}
                onClick={() => {
                  setShowPreviewModal(true)
                }}
              >
                <Visibility />
              </Fab>
            </span>
          </Tooltip>
          <Tooltip title={t`Reorder mode to use advanced schema reordering capabilities`}>
            <span>
              <Fab
                aria-label="add"
                size="medium"
                sx={{
                  bgcolor: '#fafafa',
                  boxShadow: 'none',
                  border: 'none',
                  '&.Mui-disabled': {
                    bgcolor: '#fafafa',
                  },
                }}
                disabled={activeStepIndex !== 1}
                onClick={() => {
                  setNotifications({
                    ...notifications,
                    reorderMode: !notifications.reorderMode,
                  })
                }}
              >
                {!notifications.reorderMode ? (
                  <Sort sx={{ color: 'rgba(0, 0, 0, 0.38)' }} />
                ) : (
                  <Close />
                )}
              </Fab>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
